/**
 * Pure logic for the two structural IA consolidations (no React/DOM, so it is
 * unit-testable and shared by the router, the Home/Recap pages, and the
 * sidebar):
 *
 *  1. The four overview surfaces (Dashboard `/`, My Vector `/me`, Team Vector
 *     `/team`, Team Today `/team-today`) collapse into a single **Home** at `/`
 *     with `Me` / `Team` tabs.
 *  2. **Catch Me Up** (`/catch-me-up`) + **Digest** (`/digest`) merge into a
 *     single **Recap** page at `/recap` with `Digest` / `Catch Me Up` tabs.
 *
 * Old routes are preserved as redirects (see {@link LEGACY_REDIRECTS}) so
 * bookmarks, deep links, and the command palette never 404. The `?tab=` query
 * param selects the tab, keeping deep links stable.
 */

export const HOME_PATH = '/';
export const RECAP_PATH = '/recap';

/** Query-param key used by both consolidated pages to select the active tab. */
export const TAB_PARAM = 'tab';

export type HomeTab = 'me' | 'team';
export type RecapTab = 'digest' | 'catch-me-up';

/** Resolves the Home tab from a `?tab=` value; defaults to `me`. */
export function resolveHomeTab(param: string | null | undefined): HomeTab {
  return param === 'team' ? 'team' : 'me';
}

/** Resolves the Recap tab from a `?tab=` value; defaults to `digest`. */
export function resolveRecapTab(param: string | null | undefined): RecapTab {
  return param === 'catch-me-up' ? 'catch-me-up' : 'digest';
}

export interface LegacyRedirect {
  /** The old route path that must keep working. */
  from: string;
  /** The consolidated destination (path + `?tab=` query). */
  to: string;
}

/**
 * The legacy routes that now redirect into a consolidated page + tab. The
 * router maps each of these to a `<Navigate replace>` so old links resolve to
 * the corresponding tab instead of 404ing.
 */
export const LEGACY_REDIRECTS: LegacyRedirect[] = [
  { from: '/me', to: `${HOME_PATH}?${TAB_PARAM}=me` },
  { from: '/team', to: `${HOME_PATH}?${TAB_PARAM}=team` },
  { from: '/team-today', to: `${HOME_PATH}?${TAB_PARAM}=team` },
  { from: '/digest', to: `${RECAP_PATH}?${TAB_PARAM}=digest` },
  { from: '/catch-me-up', to: `${RECAP_PATH}?${TAB_PARAM}=catch-me-up` },
];
